/**
 * chatChartMatcher — Deterministic inline-chart selector for Media Assistant.
 *
 * Instead of asking Gemini to generate chart specs (which produced generic,
 * disconnected visualizations), this module inspects the user's question and
 * the live DASHBOARD_CONTEXT to decide WHICH chart to show — then builds the
 * ChartSpec from real dashboard data.
 *
 * Approach A from YDMA-225 plan: pre-rendered dashboard widgets.
 *
 * Supported patterns:
 *  - Budget / spend allocation  → pie chart (channel cost share)
 *  - Channel comparison / ROAS   → bar chart (ROAS by channel)
 *  - CPL / cost efficiency       → bar chart (CPL by channel, lower = better)
 *  - Trends / over time          → line chart (leads or conversions trend)
 *  - MMM budget reallocation     → bar chart (current vs recommended)
 *  - Funnel stage breakdown      → bar chart (spend by stage)
 *
 * Issue: YDMA-225
 */

import type { ChartSpec } from './agentService';
import type { ChatContext, DashboardContext, MMMContext } from './mediaChatService';

// ============================================================
// KEYWORD GROUPS (Thai + English)
// ============================================================

const BUDGET_KEYWORDS = [
  'งบ', 'budget', 'spend', 'allocation', 'จัดสรร', 'สัดส่วนงบ',
  'ใช้จ่าย', 'cost breakdown', 'เท่าไหร่', 'spent',
];

const ROAS_KEYWORDS = [
  'roas', 'ผลตอบแทน', 'คุ้มไหม', 'performance', 'เปรียบเทียบช่อง',
  'compare channel', 'best channel', 'ช่องไหนดี', 'ทำงานดี',
  'underperform', 'ตัดช่อง', 'ประสิทธิภาพ',
];

const CPL_KEYWORDS = [
  'cpl', 'cost per lead', 'ต้นทุนต่อ lead', 'ราคา lead',
  'ลดต้นทุน', 'แพง', 'cpc', 'cost per click', 'efficiency',
  'ประหยัด',
];

const TREND_KEYWORDS = [
  'trend', 'เทรนด์', 'over time', 'timeline', 'เปลี่ยนแปลง',
  'growth', 'เติบโต', 'ลดลง', 'เพิ่มขึ้น', 'progress', 'รายวัน',
  'รายสัปดาห์', 'weekly', 'daily',
];

const MMM_REALLOC_KEYWORDS = [
  'realloc', 'ย้ายงบ', 'จัดสรรใหม่', 'reallocation', 'recommended',
  'แนะนำ', 'mmm', 'optimization', 'เพิ่มงบ', 'ลดงบ',
];

const STAGE_KEYWORDS = [
  'funnel', 'stage', 'awareness', 'consideration', 'conversion',
  'retention', 'ขั้นตอน', 'สายงาน', 'มาตรวัด',
];

// ============================================================
// PATTERN MATCHING HELPERS
// ============================================================

const normalize = (s: string): string => s.toLowerCase().trim();

const matchesAny = (text: string, keywords: string[]): boolean => {
  const lower = normalize(text);
  return keywords.some((kw) => lower.includes(normalize(kw)));
};

const hasMultiple = (text: string, keywordGroups: string[][]): boolean => {
  // Returns true if text matches at least 2 of the keyword groups
  return keywordGroups.filter((kw) => matchesAny(text, kw)).length >= 2;
};

// ============================================================
// CHART BUILDERS
// ============================================================

/**
 * Builds a pie chart showing budget allocation across channels.
 * Uses actual cost data from DASHBOARD_CONTEXT.
 */
const buildBudgetPieChart = (ctx: DashboardContext): ChartSpec | null => {
  const channels = ctx.channels.filter((c) => c.cost > 0);
  if (channels.length < 2) return null;

  const totalSpend = channels.reduce((s, c) => s + c.cost, 0);

  return {
    type: 'pie',
    title: `การกระจายงบโฆษณา (รวม ฿${totalSpend.toLocaleString()})`,
    data: channels.map((c) => ({
      label: c.name,
      value: c.cost,
    })),
    valueFormat: 'currency',
  };
};

/**
 * Builds a bar chart showing ROAS by channel.
 * Sorted descending so best performers are on the left.
 */
const buildRoasBarChart = (ctx: DashboardContext): ChartSpec | null => {
  // Only channels that reported conversion value. A null roas is "not measured",
  // and plotting it as a zero-height bar reads as a measured zero.
  const channels = ctx.channels.filter(
    (c): c is typeof c & { roas: number } => typeof c.roas === 'number' && c.roas > 0,
  );
  if (channels.length < 2) return null;

  const sorted = [...channels].sort((a, b) => b.roas - a.roas);

  return {
    type: 'bar',
    title: 'ROAS แยกตามช่องทาง',
    data: sorted.map((c) => ({
      label: c.name,
      value: c.roas,
    })),
    valueFormat: 'number',
  };
};

/**
 * Builds a bar chart showing CPL by channel.
 * Sorted ascending (lowest cost per click = best) so the visual naturally highlights efficiency.
 */
const buildCpcBarChart = (ctx: DashboardContext): ChartSpec | null => {
  const channels = ctx.channels.filter((c) => c.cpc > 0);
  if (channels.length < 2) return null;

  const sorted = [...channels].sort((a, b) => a.cpc - b.cpc);

  return {
    type: 'bar',
    title: 'CPC แยกตามช่องทาง (ต่ำกว่า = ดีกว่า)',
    data: sorted.map((c) => ({
      label: c.name,
      value: c.cpc,
    })),
    valueFormat: 'currency',
  };
};

/**
 * Builds a line chart from trend data.
 *
 * Prefers conversions — the outcome — then clicks, then cost per conversion.
 * The old order put `leads` first and labelled the chart "Leads รายวัน", but
 * that series held the weekly click count, and because the summary fills every
 * series with `[]` rather than leaving it undefined, `??` never fell through:
 * the first branch won every time regardless of what was in it.
 */
const buildTrendLineChart = (ctx: DashboardContext): ChartSpec | null => {
  const trends = ctx.trends;
  if (!trends) return null;

  const usable = (s: number[] | undefined): s is number[] => !!s && s.length >= 3;
  // costPerConversion is the only series that can hold a null: a bucket that
  // converted nothing has no cost per conversion. Charting that as 0 would draw
  // the account's worst week as its cheapest, so a series with a hole in it is
  // not drawn at all — the other two series have no such gap.
  const complete = (s: (number | null)[] | undefined): s is number[] =>
    !!s && s.length >= 3 && s.every((v) => typeof v === 'number');

  const choice = usable(trends.conversions)
    ? { series: trends.conversions, label: 'Conversions รายสัปดาห์', format: 'number' as const }
    : usable(trends.clicks)
    ? { series: trends.clicks, label: 'Clicks รายสัปดาห์', format: 'number' as const }
    : complete(trends.costPerConversion)
    ? { series: trends.costPerConversion, label: 'ต้นทุนต่อ conversion รายสัปดาห์', format: 'currency' as const }
    : null;
  if (!choice) return null;

  // Labelled by the days the bucket covers where the series says what they are.
  // "W1..W4" named nothing: a reader could not tell which weeks these were, nor
  // that the newest one runs up to today.
  const buckets = trends.buckets ?? [];
  const labelFor = (idx: number): string => {
    const b = buckets[idx];
    if (!b) return `W${idx + 1}`;
    return b.start === b.end ? b.start.slice(5) : `${b.start.slice(5)}–${b.end.slice(5)}`;
  };

  return {
    type: 'line',
    title: `${choice.label} (${ctx.period.label})`,
    data: choice.series.map((val: number, idx: number) => ({
      label: labelFor(idx),
      value: val,
    })),
    valueFormat: choice.format,
  };
};

/**
 * Builds a bar chart comparing current vs recommended budget (MMM context).
 */
const buildMmmReallocationBarChart = (ctx: MMMContext): ChartSpec | null => {
  const entries = ctx.budgetBreakdown.filter((b) => b.currentBudget > 0 || b.recommendedBudget > 0);
  if (entries.length < 2) return null;

  // For bar chart simplicity, show delta (recommended - current)
  return {
    type: 'bar',
    title: 'การปรับงบ: แนะนำ vs ปัจจุบัน (฿)',
    data: entries.map((b) => ({
      label: b.name,
      value: b.recommendedBudget - b.currentBudget,
    })),
    valueFormat: 'currency',
  };
};

/**
 * Builds a bar chart showing spend by funnel stage.
 */
const buildStageBarChart = (ctx: DashboardContext): ChartSpec | null => {
  const stages = ctx.stageBreakdown;
  if (!stages || stages.length < 2) return null;

  return {
    type: 'bar',
    title: 'งบโฆษณาแยกตามขั้นตอน Funnel',
    data: stages.map((s) => ({
      label: s.stageLabelTh,
      value: s.spend,
    })),
    valueFormat: 'currency',
  };
};

// ============================================================
// MAIN MATCHER
// ============================================================

/**
 * Inspects the user's question + dashboard context to determine the best
 * chart to show inline. Returns null when no chart is appropriate.
 *
 * Priority order:
 *  1. MMM reallocation (only if user explicitly asks about budget shifts)
 *  2. Budget / spend allocation pie
 *  3. ROAS comparison bar
 *  4. CPL efficiency bar
 *  5. Trend line
 *  6. Funnel stage bar
 *
 * Returns null for conversational questions that don't benefit from a chart
 * (e.g. "what is CTR?", "explain ROAS").
 */
export const matchChartToQuestion = (
  userMessage: string,
  context: ChatContext
): ChartSpec | null => {
  const msg = normalize(userMessage);

  // The insights context carries the page's conclusions, not channel series —
  // there is nothing here to plot, and inventing a chart from a capped sample
  // would draw a picture of eight rows as if it were all of them.
  if (context.kind === 'insights') return null;

  // Looker tiles ARE the charts; a second one drawn from 20 capped rows would disagree with them.
  if (context.kind === 'looker') return null;

  // --- MMM context charts ---
  if (context.kind === 'mmm') {
    // Only show reallocation chart if user is asking about budget moves
    if (matchesAny(msg, MMM_REALLOC_KEYWORDS)) {
      return buildMmmReallocationBarChart(context);
    }
    return null;
  }

  // --- Dashboard context charts ---
  const ctx = context;

  // Budget allocation pie — when user asks about spend/budget/allocation
  if (matchesAny(msg, BUDGET_KEYWORDS) && !matchesAny(msg, ROAS_KEYWORDS)) {
    const chart = buildBudgetPieChart(ctx);
    if (chart) return chart;
  }

  // ROAS comparison bar — when user asks about performance/comparison/ROAS
  if (matchesAny(msg, ROAS_KEYWORDS)) {
    const chart = buildRoasBarChart(ctx);
    if (chart) return chart;
  }

  // CPL efficiency bar — when user asks about cost efficiency
  if (matchesAny(msg, CPL_KEYWORDS)) {
    const chart = buildCpcBarChart(ctx);
    if (chart) return chart;
  }

  // Trend line — when user asks about trends/growth over time
  if (matchesAny(msg, TREND_KEYWORDS)) {
    const chart = buildTrendLineChart(ctx);
    if (chart) return chart;
  }

  // Funnel stage bar — when user asks about funnel stages
  if (matchesAny(msg, STAGE_KEYWORDS)) {
    const chart = buildStageBarChart(ctx);
    if (chart) return chart;
  }

  // Multi-signal fallback: if user mentions both budget + channels, show budget pie
  if (hasMultiple(msg, [BUDGET_KEYWORDS, ROAS_KEYWORDS, CPL_KEYWORDS, ['channel', 'ช่อง']])) {
    const chart = buildBudgetPieChart(ctx);
    if (chart) return chart;
  }

  return null;
};
